import React, { FunctionComponent } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useQuery } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import { fetch_all_with_count } from "../../../../lib/v31lib";
import { loginAtom } from '../../../../lib/auth';
import { currentPracticeAtom } from '../../../../lib/practice';

import {
  Divider,
  Grid,
  Paper,
} from '@mui/material';

import {
  Button,
  Typography
} from '../../../../components/v2/styled';

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

import { User } from '../../../../models/User';
import { Advice } from '../../../../models/Advice';

import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

const { DateTime } = require("luxon");

const strip = (s?: string) => (!s ? '' : s.replace(/<[^>]*>/g, '').trim());

type Props = {
  object: User;
}

export const CoachUserWidgetsBasicAdvice: FunctionComponent<Props> = ({
  object
}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const theme = useTheme();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);

  let adviceCount = 0;

  // Coach-level widget settings: composite { cta, items } + a sibling order
  // key. Stored on login.settings.userdetails_widgets.settings.
  const widgetSettings = login?.settings?.userdetail_widgets?.settings;
  const cfg = widgetSettings?.[HelpersClientWidgets.basicAdviceValueKey];
  const showCta = HelpersClientWidgets.getBasicAdviceCtaEnabled(cfg);
  const orderedItems = HelpersClientWidgets.getOrderedBasicAdviceItems(
    widgetSettings?.[HelpersClientWidgets.basicAdviceOrderKey],
    cfg?.items
  );
  const itemState = (key: string) => orderedItems.find((i) => i.key === key)?.state === 'shown';
  const fmtDay = (iso?: string) => {
    return !!iso ? DateTime.fromISO(iso).setZone("Europe/Brussels").toFormat("DDD", {locale: i18n.resolvedLanguage || 'nl'}) : '';
  }

  // Newest advice (same order as the advices file tab) + the total count in
  // this practice. `d.count` is the total regardless of per_page. Only the
  // consented body needs it; the count row falls back to the object field.
  const {
    data: newestAdvice,
  } = useQuery({
    queryKey: ["client", "basic_advice_last", object?.id],
    queryFn: () =>
      fetch_all_with_count<Advice>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'advice',
          fields: ['id', 'advice_date', 'goals', 'feedback_goals', 'deals', 'feedback_deals', 'education', 'feedback_education', 'next_time', 'weight', 'created_at', 'updated_at'],
          order: 'advice_date DESC, episode DESC, idx DESC, id DESC',
          per_page: 1,
          page: 0,
          filter: {
            search: '',
            advanced: {
              user_id: object?.id,
              practice_id: currentPractice?.id
            }
          }
        },
        login
      ),
    select: (d) => {
      adviceCount = d.count;
      return d.result[0];
    },
    enabled: !!object?.id && !!currentPractice?.id && !!object.consent_status?.is_intake,
  });

  return <Paper sx={{padding: 2, marginTop: 1, position: 'relative'}}>
    {/* CTA: small text button top-right, absolute in the title row. No
        premium lock — the file page's tabs gate their own modules. */}
    {showCta && <Button
      label={t("coach.general.actions.advices")}
      id="goto_advices"
      text
      size="small"
      startIcon={<ArrowRightAltIcon />}
      sx={{position: 'absolute', top: 4, right: 4, marginTop: 0, zIndex: 2}}
      onClick={() => {
        navigate(`/user/${object.id}/file`, {state: {tab: 2}});
      }}
    />}

    <Grid container spacing={0.5} sx={{"p > p": {marginTop: 0, marginBottom: 0}}}>
      <Grid item xs={12}>
        <Typography variant="h5" html>{t("coach.submenu.detail.basic_advice")}</Typography>
        <Divider />
      </Grid>

      {/* No intake consent: only the fixed warning. The rows below render
          solely in the consented state. */}
      {!object.consent_status?.is_intake && <Grid item xs={12}>
        <Typography sx={{color: theme.palette.warning.main, fontSize: '0.7rem', fontStyle: 'italic'}} html>{t("coach.consents.no_access")}</Typography>
      </Grid>}

      {/* The 6 orderable rows, in the coach's saved order. Hidden items are
          skipped entirely (reachable on the advices page). */}
      {!!object.consent_status?.is_intake && orderedItems.map((item) => {
        if (item.state !== 'shown') return null;
        switch (item.key) {
          case 'count':
            return <Grid item xs={12} key={item.key}>
              <Typography variant="body2" html>
                {`${t("coach.submenu.detail.advices_count", {count: adviceCount || object?.practice_advices_count || 0})} · ${t('coach.submenu.detail.advices_last_date', {date: fmtDay(newestAdvice?.advice_date)})}`}
              </Typography>
            </Grid>;
          case 'goals': case 'deals': case 'education': {
            const value = newestAdvice?.[item.key as 'goals' | 'deals' | 'education'];
            const feedback = newestAdvice?.[`feedback_${item.key}` as 'feedback_goals' | 'feedback_deals' | 'feedback_education'];
            if (!strip(value)) return null;
            return <React.Fragment key={item.key}>
              <Grid item xs={12}>
                <Typography variant="subtitle2">{t(`coach.models.advices.${item.key}`)}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" html sx={{whiteSpace: 'pre-wrap', paddingLeft: 2}}>{value || ''}</Typography>
              </Grid>
              {!!strip(feedback) && <Grid item xs={12}>
                <Typography variant="caption" sx={{color: 'text.secondary'}}>{t(`coach.models.advices.feedback_${item.key}`)}</Typography>
                <Typography variant="caption" html sx={{fontStyle: 'italic', color: 'text.secondary', "& p": {marginTop: 0, marginBottom: 0, paddingLeft: 2}}}>{feedback}</Typography>
              </Grid>}
            </React.Fragment>;
          }
          case 'next_time': {
            const value = newestAdvice?.next_time;
            if (!strip(value)) return null;
            return <React.Fragment key={item.key}>
              <Grid item xs={12}>
                <Typography variant="subtitle2">{t("coach.models.advices.next_time")}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" html sx={{whiteSpace: 'pre-wrap', paddingLeft: 2}}>{value}</Typography>
              </Grid>
            </React.Fragment>;
          }
          case 'weight': {
            const value = newestAdvice?.weight;
            if (!value) return null;
            return <Grid item xs={12} key={item.key}>
              <Typography variant="body2" html>{`${t("coach.models.advices.weight")}: ${value || ''} kg`}</Typography>
            </Grid>;
          }
          default:
            return null;
        }
      })}

    </Grid>
  </Paper>;
}
